import { computed, observable } from 'mobx';
import moment from 'moment-timezone';
import 'moment-duration-format';

const modeType = {
  new: 'new',
  edit: 'edit',
};

const getEditableDate = (date, tz) => {
  if (!date) return null;

  if (!tz) return moment(date).toDate();

  return new Date(moment.tz(date, tz).format('YYYY-MM-DDTHH:mm:ss'));
};

export default class RoundEditStore {
  originalRound = null;

  // editable round object, date properties are converted to Date objects using moment(value).toDate()
  @observable round = {};

  config;

  // JSON object coming from server
  constructor(round, config, timezone, isNew = false) {
    this.originalRound = Object.assign({}, round);
    this.config = config;
    this.timezone = timezone;

    this.round = observable(this.originalRound);
    // convert date fields
    this.round.startsAt = getEditableDate(this.round.startsAt, timezone);
    this.round.endsAt = getEditableDate(this.round.endsAt, timezone);
    this.round.offerValidityUntil = getEditableDate(
      this.round.offerValidityUntil,
      timezone
    );

    if (this.mode === modeType.new || isNew) {
      // set default initial values
      this.round.type =
        (!isNew && this.round.type !== 'none' ? this.round.type : undefined) ||
        this.config.roundTypes[0];
      this.round.status = 'notStarted';

      this.round.durationType = 'fixed';
      this.round.durationInDays = this.config.roundDefaultDurationInDays;
      this.round.offerValidityInDays =
        this.config.roundDefaultDurationInDays +
        this.config.roundDefaultOfferValidityExtensionInDays;
      const now = moment().hour(12).minute(0);
      const duration = moment(now).add(
        this.config.roundDefaultDurationInDays,
        'd'
      );

      this.round.startsAt = this.defaultStartsAt(this.round.type);
      this.round.showInvitedCustomersCount = true;

      if (this.round.type) this.round.endsAt = duration.toDate();

      this.round.offerValidityUntil = !this.config
        .roundDefaultOfferValidityExtensionInDays
        ? null
        : moment(duration)
            .add(this.config.roundDefaultOfferValidityExtensionInDays, 'd')
            .toDate();

      if (this.allowConditions) {
        this.round.allowConditions = true;
      }
    }
  }

  @computed get defaultStartsAt() {
    return (type) => {
      const now = moment().hour(12).minute(0);
      return this.config.roundDefaultPreviewDays && type !== 'simple_offer'
        ? moment(now).add(this.config.roundDefaultPreviewDays, 'd').toDate()
        : null;
    };
  }

  @computed get allowConditions() {
    return (this.config.allowConditionsInRounds || []).includes(
      this.round.type
    );
  }

  @computed get allowConditionsForRound() {
    return (type) => {
      return (this.config.allowConditionsInRounds || []).includes(type);
    };
  }

  @computed get showAllowConditionsToggle() {
    return this.allowConditions && this.round.type === 'incremental_auction';
  }

  // returns JSON object ready to be posted to server
  @computed get saveableRound() {
    let round = Object.assign({}, this.round);
    round.roundType = round.type;
    if (round.durationType === 'flexible') {
      // keeping: durationInDays, offerValidityInDays
      delete round.endsAt;
      delete round.offerValidityUntil;
    } else {
      // keeping: endsAt, offerValidityUntil
      round.startsAt = round.startsAt
        ? round.startsAt.toStringFormat('YYYY-MM-DDTHH:mm')
        : '';
      round.endsAt = round.endsAt
        ? round.endsAt.toStringFormat('YYYY-MM-DDTHH:mm')
        : '';
      if (round.offerValidityUntil) {
        round.offerValidityUntil =
          round.offerValidityUntil.toStringFormat('YYYY-MM-DDTHH:mm');
      }
      delete round.durationInDays;
      delete round.offerValidityInDays;
    }

    if (round.type === 'simple_offer') {
      delete round.durationInDays;
      delete round.endsAt;
    }

    if (round.status === 'opened') {
      const original = getEditableDate(
        this.originalRound.endsAt,
        this.timezone
      );
      round.sendNotification =
        this.originalRound.endsAt !== null &&
        !moment(original).isSame(moment(round.endsAt));
    }

    return round;
  }

  @computed get isValid() {
    return (
      !!this.round.type &&
      this.round.type !== 'none' &&
      this.areRoundDatesValid &&
      this.isStartingPriceValid &&
      this.isStartPriceValid &&
      this.isMinPriceValid
    );
  }

  @computed get areRoundDatesValid() {
    if (this.round.durationType === 'flexible')
      return (
        parseInt(this.round.offerValidityInDays, 0) >= 0
        // parseInt(this.round.durationInDays, 0)
      );
    if (this.round.startsAt && this.round.startsAt >= this.round.endsAt)
      return false;
    if (
      this.round.offerValidityUntil &&
      this.round.offerValidityUntil <= this.round.endsAt
    )
      return false;
    return true;
  }

  // legacy startPrice validation (should be called isEstimateValid)
  @computed get isStartingPriceValid() {
    const startPrice = Number(this.round.startPrice);
    const estimate = Number(this.round.estimate);
    return (
      this.round.type !== 'sealed_bidding' ||
      Number.isNaN(startPrice) ||
      startPrice === 0 ||
      Number.isNaN(estimate) ||
      estimate === 0 ||
      estimate >= startPrice
    );
  }

  @computed get isStartPriceValid() {
    const startPrice = Number(this.round.startPrice);
    const isAuction =
      this.round.type === 'open_bidding' ||
      this.round.type === 'incremental_auction';
    return isAuction ? !Number.isNaN(startPrice) && startPrice > 0 : true;
  }

  @computed get startPriceError() {
    const startPrice = Number(this.round.startPrice);
    const isAuction =
      this.round.type === 'open_bidding' ||
      this.round.type === 'incremental_auction';
    if (isAuction && (Number.isNaN(startPrice) || startPrice <= 0)) {
      return 'invalid starting price';
    }
    return this.isStartingPriceValid ? '' : 'estimate lower than startprice';
  }

  @computed get isMinPriceValid() {
    return !this.minPriceError;
  }

  @computed get minPriceError() {
    const startPrice = Number(this.round.startPrice);
    const minPrice = Number(this.round.minPrice);

    if (this.round.type !== 'incremental_auction') return undefined;

    if (!this.round.enableFlexibleBidding) return undefined;

    if (!this.round.biddingBelowStartingPrice) return undefined;

    if (Number.isNaN(startPrice)) return undefined;

    if (minPrice >= startPrice) return 'min price lower than start';

    return undefined;
  }

  @computed get mode() {
    return this.round.status &&
      (this.round.status === 'notStarted' || this.round.status === 'closed')
      ? 'new'
      : 'edit';
  }

  @computed get canChangeType() {
    var result =
      !!this.round.number &&
      (this.round.number <= 1 || this.config.canChangeRoundType) &&
      !this.isOpen;

    return result;
  }

  @computed get isStarted() {
    return !!this.round.status && this.round.status === 'started';
  }

  @computed get isOpen() {
    return !!this.round.status && this.round.status === 'opened';
  }

  @computed get isClosed() {
    return !!this.round.status && this.round.status === 'closed';
  }

  @computed get type() {
    return !!this.round.type ? this.round.type : undefined;
  }

  @computed get isOpenBidding() {
    return this.type === 'open_bidding';
  }

  @computed get isSimpleOffer() {
    return this.type === 'simple_offer';
  }
}



// WEBPACK FOOTER //
// ./src/app/containers/Sale/RoundSection/stores/RoundEditStore.js